import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Home', href: '#home' },
  { label: 'Projects', href: '#projects' },
  { label: 'WebGPU', href: '#webgpu' },
  { label: 'About', href: '#about' },
  { label: 'Skills', href: '#skills' },
];

export default function NavigationBanner() {
  const [activeSection, setActiveSection] = useState('Home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  // IntersectionObserver to dynamically update active section on scroll
  useEffect(() => {
    const sectionIds = navItems.map(item => item.href.replace('#', ''));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            const matchingItem = navItems.find(item => item.href === `#${sectionId}`);
            if (matchingItem) {
              setActiveSection(matchingItem.label);
            }
          }
        });
      },
      {
        rootMargin: '-20% 0px -60% 0px', // Trigger when section is ~20% from top
        threshold: 0
      }
    );

    // Observe all sections
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveSection('Home');
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (label: string, href: string) => {
    setActiveSection(label);
    setIsMobileMenuOpen(false);

    if (href === '#home') {
      scrollToTop();
      return;
    }

    // Small timeout to allow menu to start closing before scrolling
    // This often fixes mobile scroll interruptions
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <>
      {/* Navigation */}
      <nav className="fixed top-4 sm:top-6 z-50 w-[calc(100%-2rem)] sm:w-full max-w-4xl left-1/2 -translate-x-1/2">
        <div
          className={`backdrop-blur-xl shadow-2xl transition-all duration-300 ${isMobileMenuOpen ? 'rounded-2xl' : 'rounded-2xl sm:rounded-3xl'} ${theme === 'dark'
            ? 'bg-white/10 border border-white/20'
            : 'bg-black/5 border border-black/10'
            }`}
          style={{
            boxShadow: theme === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
              : '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Header Row */}
          <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-5">
            {/* Logo - Clickable to scroll to top */}
            <button
              onClick={scrollToTop}
              className="flex items-center gap-2 group"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[var(--color-uf-blue)] to-[var(--color-uf-orange)] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                <span className="text-white text-sm sm:text-base font-semibold tracking-[0.2em]">VM</span>
              </div>
              <span className="text-foreground hidden sm:block font-medium tracking-wide">Vansh Mittal</span>
            </button>

            {/* Desktop Nav Items */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item.label, item.href)}
                  className={`
                    px-4 py-2.5 rounded-xl transition-all duration-300
                    ${activeSection === item.label
                      ? 'bg-white/10 text-foreground shadow-[inset_0_0_10px_rgba(255,255,255,0.1)]'
                      : 'text-foreground/70 hover:text-foreground hover:bg-white/5'
                    }
                  `}
                >
                  {item.label}
                </button>
              ))}

              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="ml-2 p-2.5 rounded-xl hover:bg-white/10 transition-all duration-300"
                aria-label="Toggle theme"
              >
                <motion.div
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5 text-foreground/70 hover:text-foreground" />
                  ) : (
                    <Moon className="w-5 h-5 text-foreground/70 hover:text-foreground" />
                  )}
                </motion.div>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-xl hover:bg-foreground/10 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-foreground" />
              ) : (
                <Menu className="w-5 h-5 text-foreground" />
              )}
            </button>
          </div>

          {/* Mobile Menu - Integrated into navbar */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden md:hidden border-t border-white/10"
              >
                <div className="py-2 relative z-[60]">
                  {navItems.map((item, index) => (
                    <button
                      key={item.label}
                      onClick={() => handleNavClick(item.label, item.href)}
                      className={`
                        w-full px-6 py-3.5 text-left transition-all duration-200 flex items-center justify-between cursor-pointer
                        ${activeSection === item.label
                          ? (theme === 'dark' ? 'bg-white/10 text-white' : 'bg-black/10 text-foreground')
                          : (theme === 'dark' ? 'text-white/70 hover:text-white hover:bg-white/5' : 'text-foreground/70 hover:text-foreground hover:bg-black/5')
                        }
                        ${index !== navItems.length - 1 ? (theme === 'dark' ? 'border-b border-white/[0.06]' : 'border-b border-black/[0.06]') : ''}
                      `}
                    >
                      <span className="text-base">{item.label}</span>
                      {activeSection === item.label && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-uf-blue)]" />
                      )}
                    </button>
                  ))}

                  {/* Mobile Theme Toggle */}
                  <button
                    onClick={toggleTheme}
                    className={`w-full px-6 py-3.5 text-left transition-all duration-200 flex items-center justify-between cursor-pointer border-t ${theme === 'dark' ? 'border-white/[0.06] text-white/70 hover:text-white hover:bg-white/5' : 'border-black/[0.06] text-foreground/70 hover:text-foreground hover:bg-black/5'}`}
                  >
                    <span className="text-base">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                    {theme === 'dark' ? (
                      <Sun className="w-4 h-4" />
                    ) : (
                      <Moon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Backdrop for mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-40 md:hidden pointer-events-auto"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}


